import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Crown, Trophy } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const PERIODS = [
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "all", label: "All-Time" },
];

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function Leaderboard() {
  const { user } = useAuth();
  const isMP = user?.role === "managing_partner";
  const [period, setPeriod] = useState("month");
  const [scope, setScope] = useState("firm"); // firm | team (MP only)
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get(`/leaderboard?period=${period}&scope=${scope}`).then((r) => setRows(r.data));
  }, [period, scope]);

  const podiumColors = ["#2563eb", "#e4e4e7", "#fdba74"];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Team</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-1 flex items-center gap-3">
            Leaderboard <Trophy weight="fill" className="text-[#2563eb]" />
          </h1>
          <p className="text-zinc-600 mt-2">Points win. Cases paid win bigger. Dial hard.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isMP && (
            <div className="brut bg-white p-1.5 inline-flex" data-testid="scope-toggle">
              <button
                onClick={() => setScope("firm")}
                className={`px-4 py-2 font-bold uppercase text-xs tracking-widest ${scope === "firm" ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`}
                data-testid="scope-firm"
              >Firm</button>
              <button
                onClick={() => setScope("team")}
                className={`px-4 py-2 font-bold uppercase text-xs tracking-widest ${scope === "team" ? "bg-[#2563eb] text-white" : "text-black hover:bg-zinc-100"}`}
                data-testid="scope-team"
              >My Team</button>
            </div>
          )}
          <div className="brut bg-white p-1.5 inline-flex" data-testid="period-toggle">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-4 py-2 font-bold uppercase text-xs tracking-widest ${period === p.key ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`}
                data-testid={`period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Podium */}
      {rows.length >= 1 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {rows.slice(0, 3).map((r, i) => (
            <motion.div
              key={r.agent_id}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="brut p-5 bg-white relative"
              style={{ boxShadow: `6px 6px 0 0 ${podiumColors[i]}` }}
              data-testid={`podium-${i + 1}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-display font-black text-6xl tracking-tighter leading-none" style={{ color: podiumColors[i] === "#e4e4e7" ? "#a1a1aa" : podiumColors[i] }}>
                  #{i + 1}
                </div>
                {i === 0 && <Crown size={32} weight="fill" className="text-[#2563eb]" />}
              </div>
              <div className="mt-4">
                <div className="font-bold text-xl">{r.agent_name}{r.agent_id === user.id && <span className="ml-2 text-xs bg-[#2563eb] border-2 border-black px-1.5 py-0.5">YOU</span>}</div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">{r.cases_submitted || 0} subm · {r.cases_paid || 0} paid</div>
              </div>
              <div className="mt-4 flex items-end justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Points</div>
                  <div className="font-display font-black text-4xl tracking-tighter">{r.points}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Paid Prem.</div>
                  <div className="font-bold">{fmt$(r.paid_premium_amt)}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Full table */}
      <div className="brut bg-white overflow-x-auto">
        <table className="w-full border-collapse" data-testid="leaderboard-table">
          <thead>
            <tr className="bg-[#09090b] text-white">
              <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">#</th>
              <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Agent</th>
              <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Calls</th>
              <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Contacts</th>
              <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Appts Set</th>
              <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Cases Subm.</th>
              <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Cases Paid</th>
              <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Paid Prem.</th>
              <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Points</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.agent_id} className={`border-b-2 border-black last:border-b-0 ${r.agent_id === user.id ? "bg-[#dbeafe]" : "hover:bg-[#f4f4f5]"}`}>
                <td className="px-3 py-3 font-display font-black">{r.rank}</td>
                <td className="px-3 py-3 font-bold">{r.agent_name}{r.agent_id === user.id && <span className="ml-2 text-[10px] bg-[#2563eb] border-2 border-black px-1.5 py-0.5 font-bold uppercase">you</span>}</td>
                <td className="px-3 py-3 text-right">{r.calls || 0}</td>
                <td className="px-3 py-3 text-right">{r.contacts || 0}</td>
                <td className="px-3 py-3 text-right">{(r.appts_set_indiv || 0) + (r.appts_set_week || 0)}</td>
                <td className="px-3 py-3 text-right">{r.cases_submitted || 0}</td>
                <td className="px-3 py-3 text-right">{r.cases_paid || 0}</td>
                <td className="px-3 py-3 text-right">{fmt$(r.paid_premium_amt)}</td>
                <td className="px-3 py-3 text-right font-display font-black text-lg">{r.points}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-zinc-500">No data yet. Start logging.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
